import React, { useState } from 'react';
import { Package, Mail, Lock, Eye, EyeOff, RefreshCw, AlertCircle, CheckCircle, ArrowLeft, Github } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { supabase } from '../lib/supabase';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AuthPage = () => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { signIn, signUp, resetPassword, updatePassword } = useAuth();

  const validatePassword = (pass) => {
    const errors = [];
    if (pass.length < 8) errors.push('8+ chars');
    if (!/[A-Z]/.test(pass)) errors.push('uppercase');
    if (!/[a-z]/.test(pass)) errors.push('lowercase');
    if (!/[0-9]/.test(pass)) errors.push('number');
    if (!/[^A-Za-z0-9]/.test(pass)) errors.push('special char');
    return errors;
  };

  const signInWithOAuth = async (provider) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/` }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) throw error;
      } else if (mode === 'signup') {
        const errs = validatePassword(password);
        if (errs.length > 0) throw new Error(`Need: ${errs.join(', ')}`);
        if (password !== confirmPassword) throw new Error('Passwords do not match');
        const { error } = await signUp(email, password);
        if (error) throw error;
        setSuccess('Check your email to confirm!');
        setMode('login');
      } else if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setSuccess('Reset link sent to your email!');
      } else if (mode === 'reset') {
        const errs = validatePassword(password);
        if (errs.length > 0) throw new Error(`Need: ${errs.join(', ')}`);
        if (password !== confirmPassword) throw new Error('Passwords do not match');
        const { error } = await updatePassword(password);
        if (error) throw error;
        setSuccess('Password updated!');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = () => validatePassword(password).length === 0 ? 5 : 5 - validatePassword(password).length;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Package className="w-16 h-16 mx-auto mb-4 text-blue-400" />
          <h1 className="text-3xl font-bold">Minimarket</h1>
          <p className="text-gray-400 mt-2">
            {mode === 'login' && 'Sign in to your account'}
            {mode === 'signup' && 'Create a new account'}
            {mode === 'forgot' && 'Reset your password'}
            {mode === 'reset' && 'Set a new password'}
          </p>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          {mode !== 'login' && mode !== 'reset' && (
            <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </button>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-500 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-900/50 border border-green-500 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <p className="text-sm text-green-200">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode !== 'reset' && (
              <div>
                <label className="text-sm text-gray-400 block mb-1">Email</label>
                <div className="relative">
                  <Mail className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-700 rounded-lg" placeholder="you@example.com" required />
                </div>
              </div>
            )}

            {mode !== 'forgot' && (
              <div>
                <label className="text-sm text-gray-400 block mb-1">{mode === 'reset' ? 'New Password' : 'Password'}</label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 bg-gray-700 rounded-lg" placeholder="••••••••" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {(mode === 'signup' || mode === 'reset') && password && (
                  <div className="mt-2">
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map(i => <div key={i} className={`h-1 flex-1 rounded ${i <= passwordStrength() ? 'bg-green-500' : 'bg-gray-600'}`} />)}
                    </div>
                    {validatePassword(password).length > 0 && <p className="text-xs text-gray-500">Need: {validatePassword(password).join(', ')}</p>}
                  </div>
                )}
              </div>
            )}

            {(mode === 'signup' || mode === 'reset') && (
              <div>
                <label className="text-sm text-gray-400 block mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={`w-full pl-10 pr-4 py-3 bg-gray-700 rounded-lg ${confirmPassword && password !== confirmPassword ? 'ring-2 ring-red-500' : ''}`} placeholder="••••••••" required />
                </div>
              </div>
            )}

            {mode === 'login' && (
              <div className="text-right">
                <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-sm text-blue-400 hover:text-blue-300">Forgot password?</button>
              </div>
            )}

            <button type="submit" disabled={loading} className="w-full py-3 bg-blue-600 rounded-lg hover:bg-blue-500 disabled:opacity-50 flex items-center justify-center gap-2 font-semibold">
              {loading && <RefreshCw className="w-5 h-5 animate-spin" />}
              {mode === 'login' && 'Sign In'}
              {mode === 'signup' && 'Create Account'}
              {mode === 'forgot' && 'Send Reset Link'}
              {mode === 'reset' && 'Update Password'}
            </button>
          </form>

          {(mode === 'login' || mode === 'signup') && (
            <>
              <div className="mt-6 relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-700"></div></div>
                <div className="relative flex justify-center text-sm"><span className="px-2 bg-gray-800 text-gray-500">Or continue with</span></div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button type="button" onClick={() => signInWithOAuth('google')} disabled={loading} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50">
                  <GoogleIcon /><span>Google</span>
                </button>
                <button type="button" onClick={() => signInWithOAuth('github')} disabled={loading} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-700 rounded-lg hover:bg-gray-600 disabled:opacity-50">
                  <Github className="w-5 h-5" /><span>GitHub</span>
                </button>
              </div>
              <div className="mt-6 text-center text-sm text-gray-400">
                {mode === 'login' ? (
                  <>Don't have an account? <button onClick={() => { setMode('signup'); setError(''); }} className="text-blue-400 hover:text-blue-300 font-semibold">Sign up</button></>
                ) : (
                  <>Already have an account? <button onClick={() => { setMode('login'); setError(''); }} className="text-blue-400 hover:text-blue-300 font-semibold">Sign in</button></>
                )}
              </div>
            </>
          )}
        </div>
        <p className="text-center text-xs text-gray-600 mt-6">🔒 Secured with Supabase Auth</p>
      </div>
    </div>
  );
};

export default AuthPage;
